import { ICommandBarItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPCommandBar, IPDefaultButton, IPPrimaryButton, IPText } from 'baseComponents';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelectionMode } from 'baseComponents/Utils/Selection';
import Coach from 'components/CallOuts/Coach';
import { CoachTypes } from 'components/CallOuts/CoachMarkDefinitions';
import TimelineChartTask, { TimelineTaskGroupMode } from 'components/Charts/Timeline/TimelineChartTask';
import { useCopilotApprovalReload } from 'components/Copilot/useCopilotApprovalReload';
import { useSceneAIActionsItem } from 'components/Copilot/useSceneAIActionsItem';
import BulkActionsModal, {
  BulkActionType,
  IBulkAction,
  IBulkActionAssignee,
  IBulkActionOwner,
  IBulkActionState,
  IBulkActionStateTags,
  IBulkActionTag,
  IBulkActionTaskStartDate,
  IBulkActionTaskStatus,
  ICheckboxState,
} from 'components/Dialogs/BulkActionsModal';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import { DialogErrorList } from 'components/Dialogs/DialogErrorList';
import { DialogOk } from 'components/Dialogs/DialogOk';
import PeriodFilter, { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import { QuickFilterContext, clearQuickFilter } from 'components/Filter/QuickFilter';
import { IKanbanSortProps, getKanbanSortOrder, updateTaskKanban } from 'components/Kanban/KanbanBoardSortHelper';
import KanbanBoardTasks from 'components/Kanban/KanbanBoardTasks';
import OverlayLoader from 'components/Loading/OverlayLoader';
import EntityPicker from 'components/Pickers/EntityPicker';
import { getCommandBarItemsReportContextMenu } from 'components/Reports/ReportContextMenu';
import ReportRequest from 'components/Reports/ReportRequest';
import { getEntity } from 'globalFunctions';
import {
  deleteIcon,
  editIcon,
  globalStackStylesPaddingScene,
  globalStackStylesPanelFooter,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  removeFilterIcon,
} from 'globalStyles';
import { TFunction } from 'i18next';
import { mapToTasks } from 'mappings/taskMapping';
import { ApiErrorInfo, ApiErrorLevel } from 'models/apiErrorInfo';
import Assignee from 'models/assignee';
import { AuthSchemaLineOperation } from 'models/auth/authSchemaLine';
import { PermissionTypes } from 'models/auth/rolePermission';
import ReportRequestDTO from 'models/dto/Reports/ReportRequestDTO';
import { ReportRequest16DTO } from 'models/dto/Reports/ReportRequests_16_DTO';
import { ReportRequest24DTO } from 'models/dto/Reports/ReportRequests_24_DTO';
import { ReportRequest3DTO } from 'models/dto/Reports/ReportRequests_3_DTO';
import Entity, { EntityTypes } from 'models/entity';
import { GroupMember } from 'models/groupMembers';
import { Integration } from 'models/integrations/integration';
import { Owner } from 'models/owner';
import ReportDefinition, { ReportContext, ReportType } from 'models/reports/ReportDefinition';
import { AuditYearStart } from 'models/setting';
import { TagSystemTypes } from 'models/tag';
import Task, { TaskTypes } from 'models/tasks/task';
import { TaskState } from 'models/tasks/taskHelperClasses';
import Tasks from 'models/tasks/tasks';
import { SystemTaskTypes } from 'models/tasks/taskType';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useTaskViewState } from 'components/Tasks/TaskViews/TaskViewStateContext';
import { apiAddTagToTask, apiRemoveTagFromTask } from 'services/Api/tagService';
import { apiGetTasks, apiRemoveTask, apiUpdateTask, apiUpdateTasks } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import {
  hasUserDataPermission,
  hasUserFeatureGenericManager,
  hasUserRolePermission,
} from 'services/Auth/featurePermissions';
import AppError from 'utils/appError';
import { areDifferent } from 'utils/array';
import ControllerException, { ControllerExceptionCodes } from 'utils/controllerException';
import { addDateTimeYears, getAuditStartForSetting, getCurrentAuditYear, getDateTimeDiffMinute } from 'utils/datetime';
import { isValidGuid } from 'utils/guid';
import { LocalStorageKeys, getLocalStorageData, setLocalStorageData } from 'utils/localstorage';
import { sortOnTag } from 'utils/sorting';
import { getRandomId } from 'utils/string';
import { getLastUsedTaskAuthSchemaId } from 'utils/taskSharingDefaults';
import { getUniqueTagIds } from 'utils/tagHelpers';
import { handleBulkErrors } from 'utils/task';
import FilterTasks from '../Filter/FilterTasks';
import { TaskViewFilters } from '../Filter/TaskViewFilters';
import { memberFilterCompletedKey } from '../Filter/TaskViewMemberFilter';
import SingleTask from '../SingleTask';
import { TaskFieldTypes, canDeleteTasks, canUpdateTaskField, canUpdateTasks } from '../TaskAuthHelper';
import TaskList from './TaskList';
import { TaskScheduleDetailsList } from './TaskScheduleDetailsList';
import {
  getCommandBarItemsAddFromTemplate,
  getCommandBarItemsAddTaskEvent,
  getCommandBarItemsAuditYear,
  getCommandBarItemsGroupBy,
  getCommandBarItemsScheduleLevel,
  getCommandBarItemsSwitchView,
} from './TaskViewButtons';

export enum TaskViewType {
  Board = 0,
  List = 1,
  Schedule = 2,
  Timeline = 3,
}

export enum TaskViewScheduleLevel {
  //value is used to divide number of months to get quarters / months
  Year = 12,
  Quarter = 3,
  Month = 1,
}

//
// Task view
//
interface ITasksViewBoardProps {
  taskStates?: TaskState[];
  allowColumnReorder: boolean;
  onGetNewTaskBoard?: (task: Task) => Task;
}

interface ITasksViewTimelineProps {
  onGetNewTaskTimeline?: (task: Task) => Task;
}

interface ITasksViewListProps {
  hideColumns?: string[];
  selectionMode?: IPSelectionMode;
  cacheKeySort: LocalStorageKeys;
  onChangeSelection?: (tasks: Task[]) => void;
  onGetNewTaskList?: (template: Task | undefined, newTask: Task) => Task;
}

interface ITasksViewScheduleProps {
  hideContext?: boolean;
  allowEditSeries?: boolean;
  selectionMode?: IPSelectionMode;
  integrations?: Integration[];
  onChangeSelection?: (tasks: Task[]) => void;
  onTaskEditRecurringPattern?: (task: Task) => void;
  onCreateSchedule?: () => void;
  onUpdateTaskSeries?: (series: Task[]) => void;
}

interface ITasksViewProps {
  tasks: Task[];
  useCreateWizard?: boolean; //a task added with the Add command is created in the two-step wizard (Tasks scene tabs only)
  isLoading: boolean;
  isUpdating: boolean;
  boardProps: ITasksViewBoardProps;
  listProps: ITasksViewListProps;
  scheduleProps: ITasksViewScheduleProps;
  timelineProps: ITasksViewTimelineProps;
  filter?: string[];
  hidePeriodSelector?: boolean;
  hideQuickFilter?: boolean;
  hideViewType?: boolean;
  hideCommands?: boolean;
  initialViewType?: TaskViewType;
  initialPeriod?: IPeriodFilterData;
  initialFilter?: string[] | undefined;
  showGroupMemberFilter?: boolean;
  // The AI actions of the task scenes and a reload after a Copilot approval; a task list embedded in
  // another scene leaves it off
  showAIActions?: boolean;
  reloadKey?: string;
  quickFilterContext?: QuickFilterContext;
  filterStorageKey: LocalStorageKeys;
  // When set, the member filter is read from / written to this view's entry in
  // the server-persisted task-view layout instead of local storage.
  memberFilterViewKey?: string;
  lockedFilterValues?: string[];
  navigateExternal?: boolean;
  showRefresh?: boolean;
  hiddenReportIds?: ReportType[];
  onTaskClick?: (task: Task) => void;
  onUpdateFilter?: (filter: string[]) => void;
  onUpdatePeriod?: (start: Date | undefined, end: Date | undefined) => void;
  onUpdateViewType?: (viewType: TaskViewType) => void;
  onLoad: (
    viewType: TaskViewType,
    filter: string[],
    memberFilter: string[],
    start: Date | undefined,
    end: Date | undefined,
  ) => void;
  onUpdateTasks: (tasks: Task[]) => void;
  addExistingTaskContextEntity?: Entity;
  // Asked before a new task is built, so the caller can collect what it needs first (the audit's
  // findings tab asks which audit result the finding follows up on). Returning false cancels.
  onBeforeNewTask?: () => Promise<boolean>;
  // Called after a new task was saved and has an id: for work that can only point at a task that
  // exists, like relating it to another task.
  onTaskCreated?: (task: Task) => Promise<void>;
  // Replaces what "add existing task" does with the picked task. Without it the task is linked to
  // addExistingTaskContextEntity as ordinary task context.
  onAddExistingTaskOverride?: (task: Task) => Promise<void>;
}

const TasksView = (props: ITasksViewProps) => {
  const { t } = useTranslation(['translation', 'task', 'tasks', 'reports', 'tags', 'exceptions']);
  const appContext = useContext(AppContext);
  const history = useHistory();
  const currentUserId = appContext.user.id;
  // Per-view member filter is persisted in the server task-view layout. Read
  // through a ref so loadMemberFilter (called from the mount-only initialize)
  // always sees the current layout without a stale closure.
  const { viewMemberFilters } = useTaskViewState();
  const viewMemberFiltersRef = useRef(viewMemberFilters);
  viewMemberFiltersRef.current = viewMemberFilters;
  // Tracks the member-filter view key the loader last ran for, so the re-load
  // effect fires only on an actual view switch (mount is handled by initialize).
  const loadedMemberFilterViewKeyRef = useRef<string | undefined>(props.memberFilterViewKey);
  const [tasksDisplay, setTasksDisplay] = useState<Task[]>([]);
  const [integrationsDisplay, setIntegrationsDisplay] = useState<Integration[]>([]);
  const [kanbanGroupByField, setKanbanGroupByField] = useState<'state' | 'assignee'>('state');
  const [scheduleLevel, setScheduleLevel] = useState<TaskViewScheduleLevel>(TaskViewScheduleLevel.Month);
  const auditDateSetting: string = appContext.globalDataCache.settings.get(AuditYearStart);
  const [auditYear, setAuditYear] = useState<number>(() => getCurrentAuditYear(auditDateSetting));
  const [showColumnSelector, setShowColumnSelector] = useState<boolean>(false);

  //Get initial view type
  //1. When the view type is hidden, it will always be list
  //2. When the view type is mobile, it will always be list
  //3. When the initial view type is set, it will be that
  //4. When the view type is set in local storage, it will be that
  const getUserViewType = (): TaskViewType => {
    if (props.hideViewType || appContext.isMobileView) return TaskViewType.List;
    if (props.initialViewType !== undefined) return props.initialViewType;

    const val = getLocalStorageData(appContext, LocalStorageKeys.TaskViewType);

    return Number.parseInt(val || TaskViewType.List.toString());
  };

  const [viewType, setViewType] = useState<TaskViewType>(() => getUserViewType());
  const [selectedTask, setSelectedTask] = useState<Task | undefined>(undefined);
  //the task the Add command created: only that one is created in the wizard
  const [wizardTask, setWizardTask] = useState<Task | undefined>(undefined);
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const aiItem = useSceneAIActionsItem(selectedTasks.map((task) => task.taskId));
  const [periodStart, setPeriodStart] = useState<Date | undefined>(undefined);
  const [periodEnd, setPeriodEnd] = useState<Date | undefined>(undefined);
  const [selectedReport, setSelectedReport] = useState<ReportDefinition | undefined>(undefined);
  const [reportRequest, setReportRequest] = useState<ReportRequestDTO | undefined>(undefined);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState<boolean>(false);
  const [showDeleteTasks, setShowDeleteTasks] = useState<boolean>(false);
  const [showTaskDeleteError, setShowTaskDeleteError] = useState<number>(0);
  const [showTaskDeleteSucces, setShowTaskDeleteSucces] = useState<number>(0);
  const [showDeleteTasksResult, setShowDeleteTasksResult] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [seriesTasks, setSeriesTasks] = useState<Task[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState<string>(() => getRandomId());
  const [filter, setFilter] = useState<string[]>([]);
  const [memberFilter, setMemberFilter] = useState<string[]>([]);
  const [membershipLoadDone, setMembershipLoadDone] = useState<boolean>(false);
  const [membershipInitialized, setMembershipInitialized] = useState<boolean>(false);
  const [filterInitialized, setFilterInitialized] = useState<boolean>(false);
  const [periodFilterInitialized, setPeriodFilterInitialized] = useState<boolean>(false);
  const [showAddExistingTaskPanel, setShowAddExistingTaskPanel] = useState<boolean>(false);
  const [isLoadingExistingTaskEntities, setIsLoadingExistingTaskEntities] = useState<boolean>(false);
  const [isBusyAddingExistingTask, setIsBusyAddingExistingTask] = useState<number>(0);
  const [existingTaskEntities, setExistingTaskEntities] = useState<Entity[]>([]);
  const [existingTaskSelection, setExistingTaskSelection] = useState<Entity[]>([]);
  const [isLoadingMemberships, setIsLoadingMemberships] = useState<boolean>(false);
  const [showFilterDialog, setShowFilterDialog] = useState<boolean>(false);
  const [errorsToShow, setErrorsToShow] = useState<ApiErrorInfo[]>([]);
  const [showErrorsDialog, setShowErrorsDialog] = useState<boolean>(false);

  const loadExistingTaskEntities = async () => {
    if (isLoadingExistingTaskEntities) {
      return;
    }

    try {
      setIsLoadingExistingTaskEntities(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const taskCol = await apiGetTasks(true, undefined, undefined, accessToken, appContext.globalDataCache);
      const entities = taskCol.tasks
        .reduce<Entity[]>((items, task) => {
          if (task.taskId !== -1) {
            items.push(getEntity(task));
          }

          return items;
        }, [])
        .sort((a, b) => a.entityName.localeCompare(b.entityName));

      setExistingTaskEntities(entities);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingExistingTaskEntities(false);
    }
  };

  const isExistingTaskLinked = (task: Task): boolean => {
    const contextEntity = props.addExistingTaskContextEntity;
    if (!contextEntity) {
      return false;
    }

    //props.tasks is always fresh: the loaded picker list is fetched once and goes stale after a link
    if (props.tasks.some((shownTask) => shownTask.taskId === task.taskId)) {
      return true;
    }

    switch (contextEntity.typeOfEntity) {
      case EntityTypes.Requirement:
      case EntityTypes.Control:
        return task.controlIds?.includes(contextEntity.entityId) ?? false;
      case EntityTypes.Process:
        return task.processIds?.includes(contextEntity.entityId) ?? false;
      case EntityTypes.Objective:
        return task.objectiveIds?.includes(contextEntity.entityId) ?? false;
      case EntityTypes.Risk:
        return task.riskIds?.includes(contextEntity.entityId) ?? false;
      case EntityTypes.Asset:
        return task.assetIds?.includes(contextEntity.entityId) ?? false;
      case EntityTypes.ProcessingActivity:
        return task.processingActivityIds?.includes(contextEntity.entityId) ?? false;
      default:
        return false;
    }
  };

  const getExistingTaskDisabledState = (entity: Entity): [boolean, string | undefined] => {
    const task = entity.originalObject as Task | undefined;
    const isLinked = !!task && isExistingTaskLinked(task);

    return [isLinked, isLinked ? t('tasks:TaskView.Commands.AddExistingTaskAlreadyLinked') : undefined];
  };

  //memoized: a new array on every render would make the picker's list reset its selection
  const existingTaskPickerItems = useMemo((): Entity[] => {
    if (!props.addExistingTaskContextEntity) {
      return [];
    }

    return existingTaskEntities.filter((entity) => {
      if (entity.typeOfEntity !== EntityTypes.Task) {
        return false;
      }

      const task = entity.originalObject as Task | undefined;
      if (!task) {
        return false;
      }

      if (task.taskType === TaskTypes.Template || task.taskType === TaskTypes.EventTemplate) {
        return false;
      }

      return true;
    });
  }, [existingTaskEntities, props.addExistingTaskContextEntity]);

  const onAddExistingTask = async (entity: Entity) => {
    //isUpdating: a bulk add from the same panel may still be running
    if (!props.addExistingTaskContextEntity || isBusyAddingExistingTask || isUpdating) {
      return;
    }

    try {
      setIsBusyAddingExistingTask(entity.entityId);
      const sourceTask = entity.originalObject as Task | undefined;
      if (!sourceTask) {
        return;
      }

      // the caller may own what "adding" means: the audit's findings tab relates the task to the
      // execution task it follows up on instead of writing task context
      if (props.onAddExistingTaskOverride) {
        await props.onAddExistingTaskOverride(sourceTask);
        setShowAddExistingTaskPanel(false);

        return;
      }

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const taskToUpdate = sourceTask.clone();
      taskToUpdate.addEntity(props.addExistingTaskContextEntity);

      const updatedTask = await apiUpdateTask(taskToUpdate, false, accessToken, appContext.globalDataCache);
      if (!updatedTask) {
        return;
      }

      const updatedTasks = props.tasks.some((task) => task.taskId === updatedTask.taskId)
        ? props.tasks.map((task) => (task.taskId === updatedTask.taskId ? updatedTask : task))
        : [...props.tasks, updatedTask];

      props.onUpdateTasks(updatedTasks);
      setShowAddExistingTaskPanel(false);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsBusyAddingExistingTask(0);
    }
  };

  const onAddExistingTasks = async (entities: Entity[]) => {
    if (
      !props.addExistingTaskContextEntity ||
      props.onAddExistingTaskOverride ||
      isUpdating ||
      isBusyAddingExistingTask ||
      entities.length === 0
    ) {
      return;
    }

    const contextEntity = props.addExistingTaskContextEntity;

    try {
      setIsUpdating(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasksToUpdate: Task[] = entities
        .map((entity) => entity.originalObject as Task | undefined)
        .filter((sourceTask): sourceTask is Task => !!sourceTask)
        .map((sourceTask) => {
          const taskToUpdate = sourceTask.clone();
          taskToUpdate.addEntity(contextEntity);

          return taskToUpdate;
        });

      if (tasksToUpdate.length === 0) {
        return;
      }

      const updatedTasks = await apiUpdateTasks(tasksToUpdate, false, accessToken, appContext.globalDataCache);

      //B1: bulk is not all-or-nothing, report failed tasks by name and keep the ones that succeeded
      const errors = handleBulkErrors(updatedTasks, tasksToUpdate, t);
      if (errors.length > 0) {
        setErrorsToShow(errors);
        setShowErrorsDialog(true);
      }

      if (updatedTasks.tasks.length > 0) {
        let newTaskList = [...props.tasks];
        updatedTasks.forEach((updatedTask) => {
          newTaskList = newTaskList.some((task) => task.taskId === updatedTask.taskId)
            ? newTaskList.map((task) => (task.taskId === updatedTask.taskId ? updatedTask : task))
            : [...newTaskList, updatedTask];
        });
        props.onUpdateTasks(newTaskList);
        //keep the panel open when nothing was linked, so the user can retry after reading the errors
        setShowAddExistingTaskPanel(false);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  //On Render Footer: shows the multi-add confirm and Cancel buttons at the bottom of the panel
  const onRenderExistingTaskFooter = () => {
    return (
      <IPStack horizontal wrap styles={globalStackStylesPanelFooter} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton
            disabled={existingTaskSelection.length === 0 || isUpdating || !!isBusyAddingExistingTask}
            onClick={() => onAddExistingTasks(existingTaskSelection)}
            text={
              existingTaskSelection.length > 0
                ? `${t('translation:General.Suggestions.AddSelection')} (${existingTaskSelection.length})`
                : t('translation:General.Suggestions.AddSelection')
            }
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton
            onClick={() => setShowAddExistingTaskPanel(false)}
            text={t('translation:General.Button.Cancel')}
          ></IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Effects
  //
  useEffect(() => {
    //reset the multi-select selection whenever the panel opens or closes
    setExistingTaskSelection([]);
  }, [showAddExistingTaskPanel]);

  useEffect(() => {
    initialize(viewType);

    //clean-up
    return () => {
      setTasksDisplay([]);
      setIntegrationsDisplay([]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.initialViewType && props.initialViewType !== viewType) {
      initialize(props.initialViewType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialViewType]);

  // Custom views share a single route, so switching between them does not
  // remount TasksView. Re-load the per-view member filter when the active
  // view key changes; the mount-time load is handled by initialize.
  useEffect(() => {
    if (loadedMemberFilterViewKeyRef.current === props.memberFilterViewKey) return;
    loadedMemberFilterViewKeyRef.current = props.memberFilterViewKey;
    loadMemberFilter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.memberFilterViewKey]);

  useEffect(() => {
    //This trigger on rule 1), 3), 5) and 6)
    if (filterInitialized && membershipInitialized && periodFilterInitialized) {
      loadData(viewType, getFilter(), memberFilter, periodStart, periodEnd, auditYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    props.reloadKey,
    membershipInitialized,
    periodFilterInitialized,
    filterInitialized,
    periodStart,
    periodEnd,
    memberFilter,
  ]);

  useEffect(() => {
    const newBaseReportRequest = ReportRequestDTO.getNewRequest(appContext);

    switch (selectedReport?.id) {
      case ReportType.OperationalPlanning:
        const newReport3Request = new ReportRequest3DTO(newBaseReportRequest, t as TFunction<string[]>);
        newReport3Request.reportDefinition = selectedReport;
        tasksDisplay.forEach((task) => (task.recurringPatternSummary = task.recurringPattern.getShortSummary()));
        newReport3Request.series = mapToTasks(seriesTasks);
        newReport3Request.tasks = mapToTasks(tasksDisplay);
        setReportRequest(newReport3Request);
        break;
      case ReportType.Tasks:
        const newReport16Request = new ReportRequest16DTO(newBaseReportRequest, t as TFunction<string[]>);
        newReport16Request.reportDefinition = selectedReport;
        newReport16Request.tasks = mapToTasks(tasksDisplay);
        setReportRequest(newReport16Request);
        break;
      case ReportType.TasksAndEvents:
        const newReport24Request = new ReportRequest24DTO(newBaseReportRequest, t as TFunction<string[]>);
        newReport24Request.reportDefinition = selectedReport;
        newReport24Request.tasks = mapToTasks(tasksDisplay);
        setReportRequest(newReport24Request);
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  //
  // Init & Load
  //
  const initialize = (initialViewType: TaskViewType) => {
    //set schedule view settings
    const yearFromStorageRaw = getLocalStorageData(appContext, LocalStorageKeys.AuditYear);
    const yearFromStorage = yearFromStorageRaw ? Number.parseInt(yearFromStorageRaw) : undefined;
    const year = yearFromStorage ?? auditYear;
    setAuditYear(year);

    const scheduleLevelFromStorageRaw = getLocalStorageData(appContext, LocalStorageKeys.ScheduleLevel);
    const scheduleLevelFromStorage = scheduleLevelFromStorageRaw
      ? Number.parseInt(scheduleLevelFromStorageRaw)
      : undefined;
    setScheduleLevel(scheduleLevelFromStorage ?? scheduleLevel);

    //determine view type
    let newViewType = getUserViewType();
    if (initialViewType) {
      newViewType = initialViewType;
    }
    setViewType(newViewType);

    loadMemberFilter();

    // Parent-managed filters (TasksOverview view model) should not wait for local storage init.
    if (props.onUpdateFilter) {
      setFilterInitialized(true);
    }

    if (props.hidePeriodSelector === true) {
      setPeriodFilterInitialized(true);
    }

    //load data for the initial view type
    if (newViewType === TaskViewType.Schedule || newViewType === TaskViewType.Timeline) {
      loadData(newViewType, getFilter(), memberFilter, periodStart, periodEnd, year);
      //other view types are triggered by the period filter
    }
  };

  const loadData = useCallback(
    //Trigger the load in the following conditions:
    //1. After initialization
    //   - when period filter has loaded AND;
    //   - when groupmember filter has loaded AND;
    //   - when the normal filter has loaded
    //3. When period filter is shown & changed
    //4. When view type is changed
    //5. When member filter is changed
    //6. When the reloadKey is changed by the parent control
    (
      viewType: TaskViewType,
      filter: string[],
      memberFilter: string[],
      start: Date | undefined,
      end: Date | undefined,
      auditYear: number,
    ) => {
      switch (viewType) {
        case TaskViewType.Timeline:
          props.onLoad(viewType, filter, memberFilter, undefined, undefined);
          break;
        case TaskViewType.List:
        case TaskViewType.Board:
          props.onLoad(viewType, filter, memberFilter, start, end);
          break;
        case TaskViewType.Schedule:
          const startDate = getAuditStartForSetting(auditYear, auditDateSetting);
          const endDate = addDateTimeYears(startDate, 1);
          props.onLoad(viewType, filter, memberFilter, startDate, endDate);
          break;
      }
    },
    [auditDateSetting, props],
  );

  useCopilotApprovalReload(() => {
    if (props.showAIActions) loadData(viewType, filter, memberFilter, periodStart, periodEnd, auditYear);
  });

  const isLoading = useCallback((): boolean => {
    return props.isLoading || isLoadingMemberships;
  }, [isLoadingMemberships, props.isLoading]);

  //
  // Filter
  //
  const getFilter = useCallback((): string[] => {
    return props.filter ?? filter;
  }, [filter, props.filter]);

  const toggleFilterDialog = () => {
    setShowFilterDialog(!showFilterDialog);
  };

  //
  // Member filter
  //
  const loadMemberFilter = async () => {
    try {
      // Member filter ids (group guids, schema numbers, optionally the current
      // user id and the completed key) are read from this view's entry in the
      // server task-view layout. A view with no entry uses the default --
      // current user only.
      setMembershipInitialized(false);
      const storedMemberFilter = props.memberFilterViewKey
        ? viewMemberFiltersRef.current[props.memberFilterViewKey]
        : undefined;
      const ids = storedMemberFilter && storedMemberFilter.length > 0 ? storedMemberFilter : [currentUserId];
      let existingGroups: string[] = [];

      let groupids: string[] = [...ids];
      //filter out the current user
      if (ids.includes(currentUserId)) {
        groupids = groupids.filter((g) => g !== currentUserId);
      }
      //filter out non-guids (schemas)
      groupids = groupids.filter((g) => isValidGuid(g));

      //add the groups if needed
      let membershipInfo: GroupMember[] = [];

      if (groupids.length > 0) {
        //load the group membership
        membershipInfo = await loadGroupMembership();
        const groups = membershipInfo.map((m) => m.groupId);
        //filter out non-existent groups
        existingGroups = groupids.filter((id) => groups.includes(id));
      }

      //add the current user back
      if (ids.includes(currentUserId)) {
        existingGroups.push(currentUserId);
      }

      //add completed filter back
      if (ids.includes(memberFilterCompletedKey)) {
        existingGroups.push(memberFilterCompletedKey);
      }

      //add schemas back that exist in the cache
      existingGroups.push(
        ...ids.filter((g) => !isNaN(Number(g)) && appContext.globalDataCache.authSchemas.has(Number(g))),
      );

      //when all groups are unselected, add the current user
      if (existingGroups.filter((g) => isValidGuid(g)).length === 0) {
        existingGroups.push(currentUserId);
      }

      setMemberFilter(existingGroups);
    } catch (err) {
      appContext.setError(err);
    } finally {
      setMembershipInitialized(true);
    }
  };

  const loadGroupMembership = async (): Promise<GroupMember[]> => {
    if (isLoadingMemberships || membershipLoadDone) {
      return [];
    }

    let membershipInfo: GroupMember[] | undefined;

    try {
      setIsLoadingMemberships(true);

      //this sets the indication isCurrentUserMember on the groups in the cache
      membershipInfo = await appContext.globalDataCache.groups.getCurrentUserMembers();
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoadingMemberships(false);
      setMembershipLoadDone(true);
    }

    return membershipInfo ?? [];
  };

  //
  // Helpers
  //
  const onUpdateFilter = (filter: string[]) => {
    if (!filterInitialized) {
      setFilterInitialized(true);
    }
    if (props.onUpdateFilter) {
      //filter state is managed by parent component
      props.onUpdateFilter(filter);
    } else {
      //filter state is managed by this component
      setFilter(filter);
    }
  };

  const onNewTaskTimeline = async (start: Date, tagIds: number[], kind: 'task' | 'event') => {
    if (!hasUserRolePermission(appContext, PermissionTypes.CreateTask)) return;
    if (props.onBeforeNewTask && !(await props.onBeforeNewTask())) return;

    let newTask = getNewTaskTimeline(start, tagIds, kind);
    if (props.timelineProps?.onGetNewTaskTimeline) {
      newTask = props.timelineProps.onGetNewTaskTimeline(newTask);
      newTask.addEntities(newTask.relatedEntities);
    }

    setSelectedTask(newTask);
  };

  const onNewTaskBoard = async (statusIndex: number, taskList: Task[], statusObj: TaskState | Assignee) => {
    if (props.onBeforeNewTask && !(await props.onBeforeNewTask())) return;

    let newTask = await getNewTaskBoard(statusIndex, taskList, statusObj);
    if (props.boardProps.onGetNewTaskBoard) {
      newTask = props.boardProps.onGetNewTaskBoard(newTask);
      newTask.addEntities(newTask.relatedEntities);
    }

    setSelectedTask(newTask);
  };

  const getNewTaskTimeline = (start: Date, tagIds: number[], kind: 'task' | 'event'): Task => {
    const newTask = new Task();
    if (kind === 'event') {
      //a standalone event is a series (taskMasterId 0) that can contain instances, see Task.applyTemplate
      newTask.taskType = TaskTypes.Event;
      newTask.taskMasterId = 0;
      newTask.instances = [];
    }
    newTask.taskStates = appContext.globalDataCache.taskStates.items;
    newTask.taskStateId = newTask.taskStates[0].taskStateId;
    newTask.setNewStartDate(start);
    if (kind === 'task') {
      newTask.userId = appContext.user.id;
      newTask.user = appContext.user;
    }
    newTask.creatorId = appContext.user.id;
    newTask.creator = appContext.user;
    newTask.ownerId = appContext.user.id;
    newTask.owner = appContext.user;
    newTask.name = t('translation:Task.Name.Default');
    if (tagIds.length > 0) {
      newTask.tagIds = [...tagIds];
    }
    newTask.authSchemaId = getLastUsedTaskAuthSchemaId(appContext);

    return newTask;
  };

  const getNewTaskBoard = async (
    statusIndex: number,
    taskList: Task[],
    statusObj: TaskState | Assignee,
  ): Promise<Task> => {
    const newTask = new Task();
    newTask.taskStates = appContext.globalDataCache.taskStates.items;
    if (statusObj instanceof TaskState) {
      newTask.taskStateId = newTask.taskStates[statusIndex].taskStateId;
      newTask.userId = appContext.user.id;
    } else {
      newTask.taskStateId = newTask.taskStates[0].taskStateId;
      newTask.userId = statusObj.id;
    }
    newTask.user = appContext.globalDataCache.users.get(newTask.userId);
    newTask.creatorId = appContext.user.id;
    newTask.creator = appContext.user;
    newTask.ownerId = appContext.user.id;
    newTask.owner = appContext.user;
    newTask.name = t('translation:Task.Name.Default');
    newTask.authSchemaId = getLastUsedTaskAuthSchemaId(appContext);

    const sortProps: IKanbanSortProps<Task> = {
      appContext: appContext,
      itemList: taskList,
      sourceIndex: -1, //add new item in this list
      targetIndex: 0, //add at the top of the list
      updateItem: updateTaskKanban,
    };

    newTask.sortOrder = await getKanbanSortOrder(sortProps);

    return newTask;
  };

  //
  // Commandbar items
  //
  const getCommandBarItems = useCallback((): ICommandBarItemProps[] => {
    if (props.hideCommands) {
      return [];
    }

    const navigateToTemplates = () => {
      const url = '/admin/content/templates';
      history.push(url);
    };

    const onNewTaskList = async (template?: Task) => {
      if (props.onBeforeNewTask && !(await props.onBeforeNewTask())) return;

      let newTask = getNewTask(template);
      if (props.listProps.onGetNewTaskList) {
        newTask = props.listProps.onGetNewTaskList(template, newTask);
        newTask.addEntities(newTask.relatedEntities);
      }

      setWizardTask(template ? undefined : newTask);
      setSelectedTask(newTask);
    };

    const onNewEventList = async () => {
      if (props.onBeforeNewTask && !(await props.onBeforeNewTask())) return;

      let newTask = getNewEvent();
      if (props.listProps.onGetNewTaskList) {
        newTask = props.listProps.onGetNewTaskList(undefined, newTask);
        newTask.addEntities(newTask.relatedEntities);
      }

      setSelectedTask(newTask);
    };

    const getNewEvent = (): Task => {
      const newTask = getNewTask();
      newTask.taskType = TaskTypes.Event;
      newTask.taskMasterId = 0;
      newTask.instances = [];
      newTask.userId = undefined;
      newTask.user = undefined;
      newTask.name = t('translation:Task.Name.Default');

      return newTask;
    };

    const getNewTask = (template?: Task): Task => {
      let newTask: Task;
      if (template) {
        newTask = new Task().applyTemplate(template, appContext);
        newTask.taskStateId = newTask.getFirstState();
      } else {
        newTask = new Task();
        newTask.taskType = TaskTypes.Normal;
        newTask.taskStates = appContext.globalDataCache.taskStates.items;
        newTask.taskStateId = newTask.taskStates[0].taskStateId;
        newTask.name = t('translation:Task.Name.Default');
        newTask.ownerId = appContext.user.id;
        newTask.owner = appContext.user.clone();
        newTask.authSchemaId = getLastUsedTaskAuthSchemaId(appContext);
      }

      //default creator to current user
      newTask.creatorId = appContext.user.id;
      newTask.creator = appContext.user.clone();

      return newTask;
    };

    const items: ICommandBarItemProps[] = [];

    items.push(
      getCommandBarItemsAddTaskEvent(
        {
          className: 'isoplanner-mytasks-add',
          onAdd: onNewTaskList,
          onAddEvent: onNewEventList,
          allowAddExistingTask: props.addExistingTaskContextEntity !== undefined,
          onAddExistingTask: () => {
            setShowAddExistingTaskPanel(true);
          },
        },
        appContext,
        t,
      ),
    );

    items.push(
      getCommandBarItemsAddFromTemplate(
        {
          className: 'isoplanner-mytasks-add-template',
          onAdd: onNewTaskList,
          onNavigateToTemplates: navigateToTemplates,
        },
        appContext,
        t,
        props.navigateExternal,
      ),
    );

    // Always include the bulk actions so they remain reachable on mobile
    // via the CommandBar's automatic "..." overflow. The previous mobile
    // gate dropped them entirely, leaving Bulk edit / Remove unreachable
    // for touch users -- which Chris flagged in feedback round 2.
    items.push({
      key: 'edit',
      text: t('translation:General.Button.EditBulk'),
      iconProps: editIcon,
      disabled: selectedTasks.length === 0 || !canUpdateTasks(selectedTasks, appContext),
      onClick: () => setIsBulkModalOpen(true),
    });

    if (hasUserFeatureGenericManager(appContext)) {
      items.push({
        key: 'delete',
        text: t('tasks:TabAllTasks.Commands.Delete'),
        iconProps: deleteIcon,
        disabled: selectedTasks.length === 0 || !canDeleteTasks(selectedTasks, appContext),
        onClick: () => setShowDeleteTasks(true),
      });
    }

    if (props.showRefresh) {
      items.push({
        key: 'refresh',
        disabled: props.isLoading,
        text: t('translation:General.Button.Refresh'),
        iconProps: { iconName: 'Refresh' },
        onClick: () => {
          loadData(viewType, filter, memberFilter, periodStart, periodEnd, auditYear);
        },
      });
    }

    // Always include Reports so it remains reachable via overflow on mobile.
    items.push(
      getCommandBarItemsReportContextMenu(
        viewType === TaskViewType.Schedule ? ReportContext.Schedule : ReportContext.Tasks,
        {
          disabled: isLoading() || tasksDisplay.length === 0,
          onSelect: (report) => setSelectedReport(report),
          hiddenReportIds: props.hiddenReportIds,
        },
        t as TFunction<string[]>,
      ),
    );

    if (props.showAIActions && aiItem) items.push(aiItem);

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    aiItem,
    auditYear,
    filter,
    memberFilter,
    periodEnd,
    periodStart,
    props.hiddenReportIds,
    props.hideCommands,
    props.isLoading,
    props.addExistingTaskContextEntity,
    props.listProps,
    props.navigateExternal,
    props.showAIActions,
    props.showRefresh,
    props.tasks,
    selectedTasks,
    tasksDisplay,
    viewType,
  ]);

  const onSwitchView = useCallback(
    //This triggers on rule 4)
    (viewType: TaskViewType) => {
      setViewType(viewType);
      setLocalStorageData(appContext, LocalStorageKeys.TaskViewType, viewType.toString());
      setSelectedTasks([]);
      //notify parent
      if (props.onUpdateViewType) {
        props.onUpdateViewType(viewType);
      }
      //load data
      loadData(viewType, getFilter(), memberFilter, periodStart, periodEnd, auditYear);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auditYear, getFilter, loadData, memberFilter, periodEnd, periodStart],
  );

  const onSelectYear = useCallback(
    (year: number) => {
      setLocalStorageData(appContext, LocalStorageKeys.AuditYear, year.toString());
      setAuditYear(year);
      loadData(viewType, getFilter(), memberFilter, periodStart, periodEnd, year);
    },
    [appContext, getFilter, loadData, memberFilter, periodEnd, periodStart, viewType],
  );

  const updatePeriod = (start: Date, end: Date, init?: boolean) => {
    if (init === true && !periodFilterInitialized) {
      setPeriodFilterInitialized(true);
    }
    if (
      !periodStart ||
      !periodEnd ||
      getDateTimeDiffMinute(start, periodStart) !== 0 ||
      getDateTimeDiffMinute(end, periodEnd) !== 0
    ) {
      setPeriodStart(start);
      setPeriodEnd(end);
      //notify parent
      if (props.onUpdatePeriod) {
        props.onUpdatePeriod(start, end);
      }
    }
  };

  const updatePeriodAll = (init?: boolean) => {
    if (init === true && !periodFilterInitialized) {
      setPeriodFilterInitialized(true);
    }
    setPeriodStart(undefined);
    setPeriodEnd(undefined);
    //notify parent
    if (props.onUpdatePeriod && (periodStart || periodEnd)) {
      props.onUpdatePeriod(undefined, undefined);
    }
  };

  const getCommandBarFarItems = useCallback((): ICommandBarItemProps[] => {
    const getViewColumnSelectorButton = () => {
      return {
        key: 'columns',
        disabled: viewType !== TaskViewType.List,
        text: t('translation:General.Button.SelectColumns'),
        iconProps: { iconName: 'ColumnOptions' },
        onClick: () => setShowColumnSelector(true),
      };
    };

    const getViewQuickFilterResetButton = () => {
      return {
        key: 'resetQuickFilters',
        text: t('translation:General.Button.ResetQuickFilter'),
        iconProps: removeFilterIcon,
        onClick: () => {
          if (props.quickFilterContext) clearQuickFilter(props.quickFilterContext, appContext);
          if (props.onUpdateFilter) props.onUpdateFilter([]);
        },
      };
    };

    const items: ICommandBarItemProps[] = [];

    if (!props.hideViewType && !appContext.isMobileView) {
      const extraButtons: ICommandBarItemProps[] = [getViewColumnSelectorButton()];
      if (props.quickFilterContext && props.onUpdateFilter) {
        extraButtons.push(getViewQuickFilterResetButton());
      }

      items.push(
        getCommandBarItemsSwitchView(
          viewType,
          { disabled: isLoading() || appContext.isMobileView, onSwitchView: onSwitchView },
          t,
          extraButtons,
        ),
      );
    } else if (!appContext.isMobileView) {
      items.push({
        className: 'isoplanner-taskview-switchview',
        key: 'switchView',
        disabled: isLoading(),
        text: t('tasks:TaskView.Commands.View.Main'),
        iconProps: { iconName: 'View' },
        subMenuProps: { items: [getViewColumnSelectorButton()] },
      });
    }

    if (viewType === TaskViewType.Board) {
      items.push(getCommandBarItemsGroupBy(kanbanGroupByField, { disabled: isLoading(), onGroupBy: onGroupBy }, t));
    }

    if (viewType === TaskViewType.Schedule) {
      items.push(
        getCommandBarItemsScheduleLevel(
          scheduleLevel,
          {
            disabled: isLoading(),
            onSelectLevel: onSelectLevel,
            clearQuickFilter: () => {
              if (props.onUpdateFilter) props.onUpdateFilter([]);
            },
            appContext: appContext,
          },
          t,
        ),
      );

      items.push(
        getCommandBarItemsAuditYear(
          auditYear,
          {
            disabled: isLoading(),
            onSelectYear: onSelectYear,
            auditDateSetting: auditDateSetting,
          },
          t as TFunction<string[]>,
        ),
      );
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appContext.isMobileView,
    auditDateSetting,
    auditYear,
    getFilter,
    isLoading,
    kanbanGroupByField,
    onSelectYear,
    onSwitchView,
    props.hideViewType,
    props.onUpdateFilter,
    scheduleLevel,
    showFilterDialog,
    viewType,
  ]);

  const onGroupBy = (field: 'state' | 'assignee') => {
    setKanbanGroupByField(field);
  };

  const onSelectLevel = (level: TaskViewScheduleLevel) => {
    setLocalStorageData(appContext, LocalStorageKeys.ScheduleLevel, level.toString());
    setScheduleLevel(level);
  };

  //
  // Task operations
  //
  const handleClose = () => {
    setSelectedTask(undefined);
  };

  const handleSave = (updatedTask: Task, isNew: boolean, isCancelled: boolean) => {
    if (!isCancelled) {
      let cloneTaskList: Task[] = [];
      if (isNew) {
        // work that can only point at a task that exists (relating the new task to another one).
        // Deliberately not awaited: the save itself already succeeded, and a failure here is
        // surfaced by the caller - it must not roll back the task the user just created.
        if (props.onTaskCreated) props.onTaskCreated(updatedTask);

        if (viewType === TaskViewType.Schedule) {
          //For schedule view, we need to refresh the data to reflect changes in instances and/or series
          loadData(viewType, getFilter(), memberFilter, periodStart, periodEnd, auditYear);
        } else {
          // Optimistic add to the current view's list. The parent tab decides what to
          // fetch on refresh, so a task whose ownerId/userId does not match THIS view's
          // filter will appear here momentarily and then disappear on the next refetch.
          // Example: creating from a template that has a different ownerId while you
          // are on My Requests -- the task belongs on My Tasks (you are the assignee)
          // and not on My Requests (you are not the owner). Long-standing behaviour;
          // production has this too. Fix in the future by letting tabs pass a
          // task-belongs-here predicate to TasksView for filtering this optimistic add.
          cloneTaskList = [updatedTask, ...props.tasks];
        }
      } else {
        if (viewType === TaskViewType.Schedule || (selectedTask && updatedTask.taskId === selectedTask.taskMasterId)) {
          //1. For schedule view, we need to refresh the data to reflect changes in instances and/or series
          //2. When SingleTask returned the series because the series was changed instead of the instance
          //in this case, refresh the data so we get an updated instance back from the API
          loadData(viewType, getFilter(), memberFilter, periodStart, periodEnd, auditYear);
        } else {
          //replace the task in the array with the updated task
          props.tasks.forEach((task: Task) =>
            cloneTaskList.push(task.taskId === updatedTask.taskId ? updatedTask : task),
          );
          //when there is a new task created (case when back-end produces a new task in a recurring series) remove the selected task and add the new one
          if (selectedTask?.taskId !== updatedTask.taskId) {
            cloneTaskList = cloneTaskList.filter((task) => task.taskId !== selectedTask?.taskId);
            cloneTaskList = [updatedTask, ...cloneTaskList];
          }
        }
      }

      props.onUpdateTasks(cloneTaskList);
    }
  };

  const handleRemove = (removedTask: Task) => {
    const cloneTasksList: Task[] = props.tasks.filter(
      (task: Task) => task.taskId !== removedTask.taskId && task.taskMasterId !== removedTask.taskId,
    );
    props.onUpdateTasks(cloneTasksList);
  };

  const handleUpdate = (updatedTask: Task) => {
    //update the task in state
    const newTaskList = tasksDisplay.map((t) => (t.taskId === updatedTask.taskId ? updatedTask : t));
    setTasksDisplay(newTaskList);
  };

  const handleBulkUpdate = (updatedTasks: Tasks | Task[]) => {
    //update the tasks in state
    let newTaskList: Task[] = [...props.tasks];
    updatedTasks.forEach((updatedTask) => {
      newTaskList = newTaskList.map((t) => (t.taskId === updatedTask.taskId ? updatedTask : t));
    });
    props.onUpdateTasks(newTaskList);
  };

  const removeTask = (tasks: Task[], removedTask: Task): Task[] => {
    const cloneTasksList: Task[] = tasks.filter(
      (task: Task) => task.taskId !== removedTask.taskId && task.taskMasterId !== removedTask.taskId,
    );

    return cloneTasksList;
  };

  const onTaskClick = (task: Task) => {
    if (props.onTaskClick) {
      props.onTaskClick(task);
    } else {
      setSelectedTask(task);
    }
  };

  const onChangeSelection = (tasks: Task[]) => {
    setSelectedTasks(tasks);

    switch (viewType) {
      case TaskViewType.List:
        if (props.listProps.onChangeSelection) props.listProps.onChangeSelection(tasks);
        break;
      case TaskViewType.Schedule:
        if (props.scheduleProps.onChangeSelection) props.scheduleProps.onChangeSelection(tasks);
        break;
    }
  };

  //
  // Bulk operations
  //
  const onRemoveBulkTasks = async () => {
    if (isUpdating) {
      return;
    }

    let errCount: number = 0;
    let okCount: number = 0;
    let cloneTasksList: Task[] = [...props.tasks];

    try {
      setIsUpdating(true);
      appContext.showContentLoader();
      setShowDeleteTasks(false);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      for (let idx = 0; idx < selectedTasks.length; idx++) {
        //get the task from the main list to ensure we have the latest data
        const task = props.tasks.find((t) => t.taskId === selectedTasks[idx].taskId);
        if (!task) continue;
        if (task.isInstance() && task.taskType === TaskTypes.Normal) {
          //normal task instances can only be removed by removing the series
          errCount++;
        } else if (task.taskType === TaskTypes.Event && task.systemTaskType === SystemTaskTypes.Approval) {
          //approval tasks cannot be removed
          errCount++;
        } else {
          await apiRemoveTask(task, accessToken);
          cloneTasksList = removeTask(cloneTasksList, task);
          okCount++;
        }
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      props.onUpdateTasks(cloneTasksList);

      appContext.hideContentLoader();
      setIsUpdating(false);
      setSelectionResetKey(getRandomId());
      setShowTaskDeleteError(errCount);
      setShowTaskDeleteSucces(okCount);
      setShowDeleteTasksResult(true);
    }
  };

  const onUpdateTimelineTasks = async (tasks: Task[]) => {
    try {
      setIsUpdating(true);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tasksToUpdateBulk: Task[] = [];

      for (let taskIdx = 0; taskIdx < tasks.length; taskIdx++) {
        const updatedTask = tasks[taskIdx];
        const taskToUpdate = props.tasks.find((t) => t.taskId === updatedTask.taskId);
        if (taskToUpdate) {
          const canUpdate = hasUserDataPermission(
            appContext,
            [taskToUpdate.authSchemaId],
            AuthSchemaLineOperation.Update,
          );

          //
          // Tags
          //

          //When the task is moved to another lane, the timeline sets the new tag as the first item in the tagIds array
          const sourceTagIds = updatedTask.timelineTagKeys?.split('-')?.map((id) => Number.parseInt(id)) ?? [];
          const tagsAreDifferent = areDifferent(sourceTagIds, updatedTask.tagIds, (a, b) => a === b);

          if (tagsAreDifferent) {
            if (updatedTask.tagIds && canUpdateTaskField(taskToUpdate, canUpdate, TaskFieldTypes.Tags, appContext)) {
              if (!taskToUpdate.tagIds) taskToUpdate.tagIds = [];
              //1. Remove the old tags except when we move this task from a non-project phase to a project phase
              //   in this case we should only add the project tag
              //2. There is also a corner case when we move from a project phase with multiple tags to a non-project phase
              //   in this case, we do not remove the other tags. This is probably unwanted by the user. In this case, the task gets its own lane.
              const sourceIsProjectTag =
                appContext.globalDataCache.tags.get(sourceTagIds[0]).tagSystemType === TagSystemTypes.Project;
              const targetIsProjectTag =
                appContext.globalDataCache.tags.get(updatedTask.tagIds[0]).tagSystemType === TagSystemTypes.Project;

              if (updatedTask.timelineTagKeys && !(!sourceIsProjectTag && targetIsProjectTag)) {
                for (let idx = 0; idx < sourceTagIds.length; idx++) {
                  const tagId = sourceTagIds[idx];
                  if (taskToUpdate.tagIds.includes(tagId) && !updatedTask.tagIds.includes(tagId)) {
                    await apiRemoveTagFromTask(tagId, taskToUpdate.taskId, accessToken);
                    taskToUpdate.tagIds = taskToUpdate.tagIds.filter((id) => id !== tagId);
                  }
                }
              }

              //Add the new tags
              const newTags = appContext.globalDataCache.tags.getItemsForId(updatedTask.tagIds);
              for (let idx = 0; idx < updatedTask.tagIds.length; idx++) {
                const tag = newTags[idx];
                if (tag && !taskToUpdate.tagIds.includes(tag.tagId)) {
                  await apiAddTagToTask(tag, taskToUpdate.taskId, accessToken);
                  taskToUpdate.tagIds?.push(tag.tagId);
                }
              }
            }
          }

          //
          // Start date
          //
          if (taskToUpdate.startDateTime !== updatedTask.startDateTime) {
            if (canUpdateTaskField(taskToUpdate, canUpdate, TaskFieldTypes.Start, appContext)) {
              taskToUpdate.setNewStartDate(updatedTask.startDateTime);
              if (!tasksToUpdateBulk.some((t) => t.taskId === taskToUpdate.taskId)) {
                tasksToUpdateBulk.push(taskToUpdate);
              }
            }
          }

          //
          // Deadline
          //
          if (
            taskToUpdate.getDeadline(appContext.globalDataCache) !== updatedTask.getDeadline(appContext.globalDataCache)
          ) {
            if (canUpdateTaskField(taskToUpdate, canUpdate, TaskFieldTypes.Deadline, appContext)) {
              taskToUpdate.deadline = updatedTask.getDeadline(appContext.globalDataCache);
              if (!tasksToUpdateBulk.some((t) => t.taskId === taskToUpdate.taskId)) {
                tasksToUpdateBulk.push(taskToUpdate);
              }
            }
          }
        }
      }

      //Combine task updates into 1 API call
      if (tasksToUpdateBulk.length > 0) {
        await apiUpdateTasks(tasksToUpdateBulk, false, accessToken, appContext.globalDataCache);
        handleBulkUpdate(tasksToUpdateBulk);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const pushExecuteBulkActionsErrorNotAuthorized = (errors: ApiErrorInfo[], task: Task) => {
    const code = ControllerExceptionCodes.Code_General_NotAuthorized;
    const msg = ControllerException.getMessage(code, t);

    errors.push({
      code: code,
      controllerExceptionCode: code,
      level: ApiErrorLevel.Error,
      details: '',
      entity: getEntity(task),
      displayMessage: msg,
      message: msg,
    });
  };

  const executeBulkActions = async (actions: IBulkAction[]) => {
    try {
      setIsUpdating(true);
      setIsBulkModalOpen(false);

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);

      //don't use the selectedTasks as they might not reflect the latest data
      const tasksToProcess: Task[] = getActualSelectedTasks();

      let tasksToUpdate: Task[] = [];
      let updatedTasks: Tasks;
      const errors: ApiErrorInfo[] = [];

      for (let idx = 0; idx < actions.length; idx++) {
        const action = actions[idx];
        tasksToUpdate = [];

        switch (action.actionType) {
          case BulkActionType.Tag:
            const actionTag = action as IBulkActionTag;

            for (let idx1 = 0; idx1 < actionTag.tagsToAdd.length; idx1++) {
              const tag = actionTag.tagsToAdd[idx1];
              for (let taskIdx = 0; taskIdx < tasksToProcess.length; taskIdx++) {
                const task = tasksToProcess[taskIdx];
                const canUpdate = hasUserDataPermission(
                  appContext,
                  [task.authSchemaId],
                  AuthSchemaLineOperation.Update,
                );
                if (canUpdateTaskField(task, canUpdate, TaskFieldTypes.Tags, appContext)) {
                  if (!task.tagIds) task.tagIds = [];
                  if (!task.tagIds.includes(tag.tagId)) {
                    task.tagIds?.push(tag.tagId);

                    try {
                      await apiAddTagToTask(tag, task.taskId, accessToken);
                      tasksToUpdate.push(task);
                    } catch (err) {
                      const appErr = AppError.fromApiError(err);
                      errors.push({
                        code: appErr.code,
                        level: ApiErrorLevel.Error,
                        details: appErr.debug,
                        entity: getEntity(task),
                        displayMessage: appErr.message,
                        message: appErr.message,
                      });
                    }
                  }
                } else {
                  pushExecuteBulkActionsErrorNotAuthorized(errors, task);
                }
              }
            }

            for (let idx1 = 0; idx1 < actionTag.tagsToRemove.length; idx1++) {
              const tag = actionTag.tagsToRemove[idx1];
              for (let taskIdx = 0; taskIdx < tasksToProcess.length; taskIdx++) {
                const task = tasksToProcess[taskIdx];
                const canUpdate = hasUserDataPermission(
                  appContext,
                  [task.authSchemaId],
                  AuthSchemaLineOperation.Update,
                );
                if (canUpdateTaskField(task, canUpdate, TaskFieldTypes.Tags, appContext)) {
                  if (task.tagIds?.includes(tag.tagId)) {
                    task.tagIds = task.tagIds?.filter((id) => id !== tag.tagId);
                    try {
                      await apiRemoveTagFromTask(tag.tagId, task.taskId, accessToken);
                      tasksToUpdate.push(task);
                    } catch (err) {
                      const appErr = AppError.fromApiError(err);
                      errors.push({
                        code: appErr.code,
                        level: ApiErrorLevel.Error,
                        details: appErr.debug,
                        entity: getEntity(task),
                        displayMessage: appErr.message,
                        message: appErr.message,
                      });
                    }
                  }
                } else {
                  pushExecuteBulkActionsErrorNotAuthorized(errors, task);
                }
              }
            }

            handleBulkUpdate(tasksToUpdate);
            break;
          case BulkActionType.Owner:
            const actionOwner = action as IBulkActionOwner;

            for (let taskIdx = 0; taskIdx < tasksToProcess.length; taskIdx++) {
              const task = tasksToProcess[taskIdx];

              if (actionOwner.newOwner && task.ownerId !== actionOwner.newOwner?.id) {
                const canUpdate = hasUserDataPermission(
                  appContext,
                  [task.authSchemaId],
                  AuthSchemaLineOperation.Update,
                );
                if (canUpdateTaskField(task, canUpdate, TaskFieldTypes.Owner, appContext)) {
                  Owner.applyOwner(task, actionOwner.newOwner);
                  task.owner = actionOwner.newOwner.item;
                  tasksToUpdate.push(task);
                } else {
                  pushExecuteBulkActionsErrorNotAuthorized(errors, task);
                }
              }
            }

            updatedTasks = await apiUpdateTasks(tasksToUpdate, false, accessToken, appContext.globalDataCache);
            errors.push(...handleBulkErrors(updatedTasks, tasksDisplay, t));
            handleBulkUpdate(updatedTasks);
            break;
          case BulkActionType.Assignee:
            const actionAssignee = action as IBulkActionAssignee;

            for (let taskIdx = 0; taskIdx < tasksToProcess.length; taskIdx++) {
              const task = tasksToProcess[taskIdx];

              if (task.taskType === TaskTypes.Event) {
                //events cannot be assigned
                continue;
              }

              if (
                task.userId !== actionAssignee.newAssigneeId ||
                task.authSchemaId !== actionAssignee.newAuthSchemaId
              ) {
                const canUpdate = hasUserDataPermission(
                  appContext,
                  [task.authSchemaId],
                  AuthSchemaLineOperation.Update,
                );
                if (canUpdateTaskField(task, canUpdate, TaskFieldTypes.Assignee, appContext)) {
                  task.userId = actionAssignee.newAssigneeId;
                  task.authSchemaId = actionAssignee.newAuthSchemaId;
                  tasksToUpdate.push(task);
                } else {
                  pushExecuteBulkActionsErrorNotAuthorized(errors, task);
                }
              }
            }

            updatedTasks = await apiUpdateTasks(tasksToUpdate, false, accessToken, appContext.globalDataCache);
            errors.push(...handleBulkErrors(updatedTasks, tasksDisplay, t));
            handleBulkUpdate(updatedTasks);

            break;
          case BulkActionType.TaskStatus:
            const actionTaskStatus = action as IBulkActionTaskStatus;

            for (let taskIdx = 0; taskIdx < tasksToProcess.length; taskIdx++) {
              const task = tasksToProcess[taskIdx];

              if (actionTaskStatus.newStatus && task.taskStateId !== actionTaskStatus.newStatus) {
                const canUpdate = hasUserDataPermission(
                  appContext,
                  [task.authSchemaId],
                  AuthSchemaLineOperation.Update,
                );
                if (canUpdateTaskField(task, canUpdate, TaskFieldTypes.Status, appContext)) {
                  task.taskStateId = actionTaskStatus.newStatus;
                  tasksToUpdate.push(task);
                } else {
                  pushExecuteBulkActionsErrorNotAuthorized(errors, task);
                }
              }
            }

            updatedTasks = await apiUpdateTasks(tasksToUpdate, false, accessToken, appContext.globalDataCache);
            errors.push(...handleBulkErrors(updatedTasks, tasksDisplay, t));
            handleBulkUpdate(updatedTasks);
            break;

          case BulkActionType.StartDate:
            const actionTaskStartDate = action as IBulkActionTaskStartDate;

            for (let taskIdx = 0; taskIdx < tasksToProcess.length; taskIdx++) {
              const task = tasksToProcess[taskIdx];
              const canUpdate = hasUserDataPermission(appContext, [task.authSchemaId], AuthSchemaLineOperation.Update);

              if (canUpdateTaskField(task, canUpdate, TaskFieldTypes.Status, appContext)) {
                switch (actionTaskStartDate.operationType) {
                  case 'set':
                    if (actionTaskStartDate.newStartDate) {
                      task.setNewStartDate(actionTaskStartDate.newStartDate);
                    }
                    if (actionTaskStartDate.newStartTime) {
                      task.setNewStartTime(
                        actionTaskStartDate.newStartTime.getHours(),
                        actionTaskStartDate.newStartTime.getMinutes(),
                      );
                    }
                    if (actionTaskStartDate.newEndTime) {
                      task.setNewEndTime(
                        actionTaskStartDate.newEndTime.getHours(),
                        actionTaskStartDate.newEndTime.getMinutes(),
                      );
                    }
                    if (actionTaskStartDate.newDeadline) {
                      task.setNewDeadline(actionTaskStartDate.newDeadline);
                    }
                    if (actionTaskStartDate.newDeadlineTime) {
                      task.setNewDeadlineTime(
                        actionTaskStartDate.newDeadlineTime.getHours(),
                        actionTaskStartDate.newDeadlineTime.getMinutes(),
                      );
                    }
                    break;
                  case 'move':
                    if (actionTaskStartDate.moveStartDays !== 0) {
                      const daysToMove =
                        actionTaskStartDate.moveStartDays *
                        (actionTaskStartDate.moveStartDirectionKey === 'forward' ? 1 : -1);
                      task.move(daysToMove);
                    }
                    if (actionTaskStartDate.moveDeadlineDuration !== 0) {
                      task.setNewDeadlineDuration(actionTaskStartDate.moveDeadlineDuration);
                    }
                    break;
                }

                tasksToUpdate.push(task);
              } else {
                pushExecuteBulkActionsErrorNotAuthorized(errors, task);
              }
            }

            updatedTasks = await apiUpdateTasks(tasksToUpdate, false, accessToken, appContext.globalDataCache);
            errors.push(...handleBulkErrors(updatedTasks, tasksDisplay, t));
            handleBulkUpdate(updatedTasks);
            break;
        }
      }

      if (errors.length > 0) {
        loadData(viewType, getFilter(), memberFilter, periodStart, periodEnd, auditYear);
        setErrorsToShow(errors);
        setShowErrorsDialog(true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const getAllowedBulkActions = (): BulkActionType[] => {
    const selected = getActualSelectedTasks();
    const hasEventSelection = selected.some((t) => t.taskType === TaskTypes.Event);

    if (hasUserFeatureGenericManager(appContext)) {
      const actions: BulkActionType[] = [
        BulkActionType.Tag,
        BulkActionType.Owner,
        BulkActionType.StartDate,
        BulkActionType.TaskStatus,
      ];

      if (!hasEventSelection) {
        actions.splice(1, 0, BulkActionType.Assignee);
      }

      return actions;
    } else {
      return [BulkActionType.Tag];
    }
  };

  const getActualSelectedTasks = (): Task[] => {
    //get the latest version of the selected tasks from the main list
    const tasksToProcess: Task[] = [];
    selectedTasks.forEach((selectedTask) => {
      const task = props.tasks.find((t) => t.taskId === selectedTask.taskId);
      if (task) {
        tasksToProcess.push(task.clone());
      }
    });

    return tasksToProcess;
  };

  const getBulkActionState = async (actionType: BulkActionType): Promise<IBulkActionState | undefined> => {
    //don't use the selectedTasks as they might not reflect the latest data
    const tasksToProcess: Task[] = getActualSelectedTasks();

    switch (actionType) {
      case BulkActionType.Tag:
        const ids = getUniqueTagIds(tasksToProcess);
        const tags = appContext.globalDataCache.tags.getItemsForId(ids);
        tags.sort(sortOnTag);

        for (let idx = 0; idx < tags.length; idx++) {
          const tag = tags[idx];
          tag.usedCount = tasksToProcess.filter((r) => r.tagIds?.some((id) => id === tag.tagId)).length;
        }

        const newState: IBulkActionStateTags = {
          tags: tags,
          checkboxState: tags.map((tag) => {
            return tag.usedCount === 0
              ? ICheckboxState.unchecked
              : tag.usedCount === tasksToProcess.length
              ? ICheckboxState.checked
              : ICheckboxState.indetermined;
          }),
        };

        return newState;
      default:
        return undefined;
    }
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      {isUpdating && <OverlayLoader text={t('translation:General.Notifications.Saving')} />}
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPStack horizontal wrap horizontalAlign="end" styles={globalStackStylesPaddingScene}>
            {/* horizontalAlign="end" + minWidth:0 keep "+ New" pinned right when the
                CommandBar overflows on narrow viewports, instead of orphaning left. */}
            <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
              <Coach
                target={'#taskview-commandbar'}
                type={CoachTypes.Timeline}
                direction={IPDirectionalHint.bottomRightEdge}
              />
              {props.onUpdateFilter && !appContext.isMobileView && (
                <Coach target={'#taskviewFilterButtonAnchor'} type={CoachTypes.TaskFilterLocation} />
              )}
              <IPCommandBar
                id="taskview-commandbar"
                styles={{ root: { paddingLeft: 0 } }}
                items={getCommandBarItems()}
                farItems={getCommandBarFarItems()}
              ></IPCommandBar>
            </IPStack.Item>
            {props.onUpdateFilter && !appContext.isMobileView && (
              <IPStack.Item styles={{ root: { marginRight: 10, display: 'flex', alignItems: 'center' } }}>
                <span id="taskviewFilterButtonAnchor" style={{ display: 'inline-flex' }}>
                  <FilterTasks
                    toggleDialog={toggleFilterDialog}
                    isOpen={showFilterDialog}
                    updateSelectedFilter={props.onUpdateFilter}
                    selectedFilter={getFilter()}
                    showWorkableTypeFilter={true}
                    showTemplateFilter={true}
                  />
                </span>
              </IPStack.Item>
            )}
            {!props.hidePeriodSelector && viewType !== TaskViewType.Schedule && viewType !== TaskViewType.Timeline && (
              <IPStack.Item>
                <PeriodFilter
                  onUpdatePeriod={updatePeriod}
                  onUpdatePeriodAll={updatePeriodAll}
                  storageKey={LocalStorageKeys.TaskPeriodSelector}
                  period={props.initialPeriod}
                  showAll={true}
                />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item styles={globalStackStylesPaddingScene}>
          <TaskViewFilters
            selectedFilter={getFilter()}
            lockedFilterValues={props.lockedFilterValues}
            tasks={props.tasks}
            integrations={props.scheduleProps.integrations}
            onUpdateSelectedFilter={onUpdateFilter} //this happens also after first initialization of the filter
            onUpdateTasksDisplay={setTasksDisplay}
            onUpdateIntegrationsDisplay={setIntegrationsDisplay}
            selectedTasks={selectedTasks}
            selectedIntegrations={[]} //not implemented
            showGroupMemberFilter={props.showGroupMemberFilter}
            memberFilter={memberFilter}
            memberFilterViewKey={props.memberFilterViewKey}
            isLoadingMembership={isLoadingMemberships}
            onLoadMembership={loadGroupMembership}
            onUpdateMemberFilter={setMemberFilter}
            hideQuickFilter={props.hideQuickFilter}
            toggleFilterDialog={props.onUpdateFilter ? undefined : toggleFilterDialog}
            maxQuickFilters={props.onUpdateFilter ? 5 : 3}
            showFilterDialog={showFilterDialog}
            filterStorageKey={props.filterStorageKey}
            // Parent-managed task views own their filter state, so local storage should not overwrite it.
            // Test note: verify opening a custom view applies its persisted filter values without local-storage fallback.
            disableLocalFilterStorage={Boolean(props.onUpdateFilter)}
            quickFilterContext={props.quickFilterContext}
            initialFilter={props.initialFilter}
          />
        </IPStack.Item>
        {viewType === TaskViewType.Board && (
          <IPStack.Item grow>
            <KanbanBoardTasks
              statusField={kanbanGroupByField}
              taskStates={props.boardProps.taskStates}
              tasks={tasksDisplay}
              allowColumnReorder={props.boardProps.allowColumnReorder}
              isLoading={isLoading()}
              onTaskClick={onTaskClick}
              onNewTask={onNewTaskBoard}
              allowNew={hasUserRolePermission(appContext, PermissionTypes.CreateTask)}
            />
          </IPStack.Item>
        )}
        {viewType === TaskViewType.List && (
          <IPStack.Item grow>
            <TaskList
              isLoading={isLoading()}
              hideColumns={props.listProps.hideColumns}
              tasks={tasksDisplay}
              filter={getFilter()}
              onTaskClick={onTaskClick}
              updateFilter={onUpdateFilter}
              cacheKeySort={props.listProps.cacheKeySort}
              selectionMode={props.listProps.selectionMode}
              onChangeSelection={onChangeSelection}
              showColumnSelector={showColumnSelector}
              hideColumnSelector={() => setShowColumnSelector(false)}
              selectionResetKey={selectionResetKey}
            />
          </IPStack.Item>
        )}
        {viewType === TaskViewType.Schedule && (
          <IPStack.Item grow>
            <TaskScheduleDetailsList
              level={scheduleLevel}
              year={auditYear}
              tasksDisplay={tasksDisplay}
              isUpdating={props.isUpdating}
              isLoading={isLoading()}
              onTaskClick={onTaskClick}
              onTaskEditRecurringPattern={props.scheduleProps.onTaskEditRecurringPattern}
              hideContext={props.scheduleProps.hideContext}
              allowEditSeries={props.scheduleProps.allowEditSeries}
              filter={getFilter()}
              updateFilter={onUpdateFilter}
              selectionMode={props.scheduleProps.selectionMode}
              onChangeSelection={onChangeSelection}
              cacheKeySort={LocalStorageKeys.TaskScheduleSort}
              onUpdateTaskSeries={setSeriesTasks}
              integrations={props.scheduleProps.integrations}
              integrationsDisplay={integrationsDisplay}
            />
          </IPStack.Item>
        )}
        {viewType === TaskViewType.Timeline && (
          <IPStack.Item id="timeline-container" grow styles={{ root: { padding: 20 } }}>
            <TimelineChartTask
              tasks={tasksDisplay}
              tags={appContext.globalDataCache.tags.items}
              defaultGroupName={t('tasks:Timeline.DefaultGroup')}
              groupMode={TimelineTaskGroupMode.Tag}
              isLoading={isLoading()}
              allowEdit={true}
              height={`${(document.getElementById('timeline-container')?.clientHeight ?? 300) - 40}px`}
              onClickTask={onTaskClick}
              onAddTask={onNewTaskTimeline}
              onUpdateTasks={onUpdateTimelineTasks}
            />
          </IPStack.Item>
        )}
        {selectedTask && (
          <SingleTask
            isOpen={selectedTask !== undefined}
            close={handleClose}
            task={selectedTask}
            useCreateWizard={props.useCreateWizard === true && selectedTask === wizardTask}
            onSave={handleSave}
            onRemove={handleRemove}
            onUpdate={handleUpdate}
            navigateExternal={props.navigateExternal}
          />
        )}
        {props.addExistingTaskContextEntity && (
          <IPPanel
            type={IPPanelType.medium}
            headerText={t('tasks:TaskView.Commands.AddExistingTask')}
            isOpen={showAddExistingTaskPanel}
            isBlocking={true}
            isFooterAtBottom={!props.onAddExistingTaskOverride}
            onRenderFooterContent={!props.onAddExistingTaskOverride ? onRenderExistingTaskFooter : undefined}
            onDismiss={() => setShowAddExistingTaskPanel(false)}
          >
            <IPStack verticalFill tokens={globalStackTokensGapMedium} styles={{ root: { height: '100%' } }}>
              <IPStack.Item>
                <IPText>{t('tasks:TaskView.Commands.AddExistingTaskInfo')}</IPText>
              </IPStack.Item>
              <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
                <EntityPicker
                  entities={existingTaskPickerItems}
                  disabledEntities={getExistingTaskDisabledState}
                  addSelectedEntity={onAddExistingTask}
                  addSelectedEntities={onAddExistingTasks}
                  loadData={loadExistingTaskEntities}
                  isLoading={isLoadingExistingTaskEntities}
                  isOnPanel={true}
                  // the audit's findings tab relates the picked task to an execution task instead
                  // of writing task context, so it keeps the single-select flow
                  allowMultiAdd={!props.onAddExistingTaskOverride}
                  showHeader={!props.onAddExistingTaskOverride}
                  onSelectionChanged={setExistingTaskSelection}
                  hideAddSelectionButton={!props.onAddExistingTaskOverride}
                  isBusyAdding={isBusyAddingExistingTask}
                  compact={true}
                  panelListHeightPercentage={85}
                  panelListMaxHeightPercentage={95}
                  panelListMinHeight={520}
                />
              </IPStack.Item>
            </IPStack>
          </IPPanel>
        )}
        <DialogConfirmDelete
          hidden={!showDeleteTasks}
          onYes={onRemoveBulkTasks}
          onNo={() => setShowDeleteTasks(false)}
          title={t('tasks:TabAllTasks.Bulk.Remove.Title')}
          subText={t('tasks:TabAllTasks.Bulk.Remove.SubText')}
        />
        <DialogOk
          hidden={!showDeleteTasksResult}
          onOk={() => setShowDeleteTasksResult(false)}
          title={t('tasks:TabAllTasks.Bulk.Remove.ResultTitle')}
          subText={t('tasks:TabAllTasks.Bulk.Remove.SuccesSubText', { count: showTaskDeleteSucces })}
          optionalJSX={
            showTaskDeleteError > 0 ? (
              <IPText>{t('tasks:TabAllTasks.Bulk.Remove.ErrorSubText', { count: showTaskDeleteError })}</IPText>
            ) : undefined
          }
        />
        {isBulkModalOpen && (
          <BulkActionsModal
            title={t('tasks:TabAllTasks.Bulk.Edit.Title')}
            isOpen={isBulkModalOpen}
            onClose={() => setIsBulkModalOpen(false)}
            actionTypes={getAllowedBulkActions()}
            itemCount={selectedTasks.length}
            noAuthCount={
              getActualSelectedTasks().filter(
                (s) =>
                  s.userId !== appContext.user.id &&
                  !hasUserDataPermission(appContext, [s.authSchemaId], AuthSchemaLineOperation.Update),
              ).length
            }
            onExecute={executeBulkActions}
            onGetState={getBulkActionState}
            disableRemoveOwner={true}
            disallowOwnerRoles={true}
          />
        )}
        <ReportRequest
          isOpen={reportRequest !== undefined}
          onClose={() => setReportRequest(undefined)}
          reportRequest={reportRequest}
        />
        {showErrorsDialog && (
          <DialogErrorList
            isOpen={showErrorsDialog}
            items={errorsToShow}
            onClose={() => setShowErrorsDialog(false)}
            title={t('tasks:BulkActionErrorModal.Title')}
            subTitle={t('tasks:BulkActionErrorModal.SubTitle')}
            createTicket={true}
          />
        )}
      </IPStack>
    </IPStack>
  );
};

export default TasksView;
